import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, buildUrl, currentAccessToken } from '../client';

export type EcmrRole = 'sender' | 'carrier' | 'consignee';
export type EcmrStatus = 'draft' | 'issued' | 'delivered' | 'cancelled';

export interface EcmrSignature {
  role: EcmrRole;
  name: string;
  at: string;
  lat: number | null;
  lon: number | null;
  remarks: string | null;
  source: 'driver_app' | 'office';
}

export interface Ecmr {
  id: string;
  orderId: string;
  orderNumber: string;
  number: string;
  status: EcmrStatus;
  sender: { name: string | null; address: string | null };
  consignee: { name: string | null; address: string | null };
  loading: { place: string | null; country: string | null; date: string | null };
  delivery: { place: string | null; country: string | null };
  goods: { description: string | null; packages: number | null; weightKg: number | null };
  carrier: { name: string | null; address: string | null; vehiclePlate: string | null; driverName: string | null };
  signatures: EcmrSignature[];
  missing: string[];
  contentHash: string | null;
  hashIntact: boolean;
  updatedAt: string;
}

export interface EcmrListItem {
  id: string;
  orderId: string;
  orderNumber: string;
  number: string;
  status: EcmrStatus;
  route: string;
  signed: EcmrRole[];
  updatedAt: string;
}

export const ECMR_STATUS_LABEL: Record<EcmrStatus, string> = {
  draft: 'Чака подписи на товарене',
  issued: 'Подписана — в превоз',
  delivered: 'Приета от получателя',
  cancelled: 'Анулирана',
};

export const ECMR_ROLE_LABEL: Record<EcmrRole, string> = {
  sender: 'Изпращач',
  carrier: 'Превозвач (шофьор)',
  consignee: 'Получател',
};

export function useEcmrList(status?: EcmrStatus) {
  return useQuery({
    queryKey: ['ecmr', 'list', status ?? 'all'],
    queryFn: () => api.get<EcmrListItem[]>('/ecmr', { status }),
    staleTime: 30_000,
  });
}

export function useEcmr(orderId: string, enabled = true) {
  return useQuery({
    queryKey: ['ecmr', 'order', orderId],
    queryFn: () => api.get<Ecmr>(`/ecmr/orders/${orderId}`),
    enabled,
  });
}

export function useEcmrSign(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { role: EcmrRole; name: string; remarks?: string }) => api.post<Ecmr>(`/ecmr/orders/${orderId}/sign`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ecmr'] }),
  });
}

export function useEcmrRefresh(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Ecmr>(`/ecmr/orders/${orderId}/refresh`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ecmr'] }),
  });
}

/** PDF-ът иска вход — тегли се с токена и се отваря като файл в нов раздел. */
export async function openEcmrPdf(orderId: string): Promise<void> {
  const tab = window.open('', '_blank');
  const token = await currentAccessToken();
  const res = await fetch(buildUrl(`/ecmr/orders/${orderId}/pdf`), {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    tab?.close();
    throw new Error('PDF на товарителницата не се отвори.');
  }
  const url = URL.createObjectURL(await res.blob());
  if (tab) tab.location.href = url;
  else window.location.href = url;
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
